import { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "../lib/utils";

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export interface VoiceMessagePlayerProps {
  src: string;
  autoPlay?: boolean;
  /** Avatar or initials shown left of the waveform. */
  avatarSrc?: string;
  avatarFallback?: string;
  avatarColor?: string;
  /** Mirror the layout (sent = right-aligned). */
  sent?: boolean;
  className?: string;
}

export function VoiceMessagePlayer({
  src,
  autoPlay = false,
  avatarSrc,
  avatarFallback,
  avatarColor: avatarBg,
  sent = false,
  className,
}: VoiceMessagePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const speedIdx = useRef(0);

  // Try to resolve duration via AudioContext if metadata is missing
  const resolveRef = useRef(false);
  const resolveDuration = useCallback(async () => {
    if (resolveRef.current || !src) return;
    resolveRef.current = true;
    try {
      const res = await fetch(src);
      if (!res.ok) return;
      const buf = await res.arrayBuffer();
      const ctx = new AudioContext();
      try {
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        if (Number.isFinite(decoded.duration) && decoded.duration > 0) {
          setDuration(decoded.duration);
        }
      } finally {
        await ctx.close();
      }
    } catch {}
  }, [src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = speed;
    const onTime = () => setCurrent(a.currentTime);
    const onLoad = () => {
      const d = a.duration;
      if (Number.isFinite(d) && d > 0) setDuration(d);
      else void resolveDuration();
    };
    const onEnd = () => { setPlaying(false); setCurrent(0); a.currentTime = 0; };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    if (autoPlay) { a.play().then(() => setPlaying(true)).catch(() => {}); }
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
    };
  }, [src, autoPlay, resolveDuration, speed]);

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  }

  function cycleSpeed() {
    speedIdx.current = (speedIdx.current + 1) % SPEED_OPTIONS.length;
    const next = SPEED_OPTIONS[speedIdx.current]!;
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  }

  function handleScrub(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setCurrent(a.currentTime);
  }

  const progress = duration > 0 ? current / duration : 0;
  const timeLabel = playing ? fmt(current) : fmt(duration) || fmt(current);

  return (
    <div className={cn("vmp", sent && "vmp-sent", className)}>
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Avatar */}
      <button
        className="vmp-avatar"
        style={avatarSrc ? undefined : { background: avatarBg ?? "var(--bg-hover)" }}
        onClick={togglePlay}
        aria-label={playing ? "Pause" : "Play"}
        type="button"
      >
        {avatarSrc ? (
          <img src={avatarSrc} alt="" className="vmp-avatar-img" />
        ) : (
          <>
            <span className="vmp-avatar-initials" style={{ display: playing ? "none" : undefined }}>
              {avatarFallback ?? "?"}
            </span>
            <span className="vmp-play-icon" style={{ display: playing ? undefined : "none" }}>
              <PauseIcon />
            </span>
            <span className="vmp-play-icon" style={{ display: playing ? "none" : undefined, visibility: avatarFallback ? "hidden" : undefined }}>
              <PlayIcon />
            </span>
          </>
        )}
        {avatarSrc && (
          <span className="vmp-avatar-play-overlay">
            {playing ? <PauseIcon /> : <PlayIcon />}
          </span>
        )}
      </button>

      {/* Track */}
      <div className="vmp-body">
        <div
          className="vmp-track"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          onClick={handleScrub}
        >
          <div className="vmp-track-bg" />
          <div className="vmp-track-fill" style={{ width: `${progress * 100}%` }} />
          <div className="vmp-thumb" style={{ left: `${progress * 100}%` }} />
        </div>

        <div className="vmp-meta">
          <span className="vmp-time">{timeLabel}</span>
          <button
            className="vmp-speed"
            type="button"
            onClick={cycleSpeed}
            title="Playback speed"
          >{speed}×</button>
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
      <polygon points="3,2 12,7 3,12" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
      <rect x="2" y="2" width="4" height="10" rx="1" />
      <rect x="8" y="2" width="4" height="10" rx="1" />
    </svg>
  );
}
